import {
	Dispatch,
	FunctionComponent,
	MouseEvent,
	ReactNode,
	Ref,
	SetStateAction,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	isSendableEmailStatus,
	toEmailStatus,
	type EmailStatus,
	unhandledEmailStatus,
} from "@signalist/shared-types";

import { ReactComponent as ExternalLinkIcon } from "@assets/icons/launch.svg";

import { EmailIconProps } from "@/components/EmailIcon/EmailIcon";
import { ContactChannels } from "@/components/ContactChannels/ContactChannels";
import { EmailProvenance } from "@/components/ContactChannels/emailProvenance";
import {
	ContactChannelData,
	ContactDomain,
	emailProvenanceOf,
} from "@/components/ContactChannels/types";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { ProspectScoreBadge } from "@/components/ProspectScoreBadge/ProspectScoreBadge";
import { AboutSection } from "@/components/IdentityCard/AboutSection";
import { ExpandToggle } from "@/components/IdentityCard/ExpandToggle";
import { WorkHistoryList, WorkHistoryEmployment } from "@/components/IdentityCard/WorkHistoryList";

import { classNames } from "@/utils/classNames";
import { ensureHttpProtocol } from "@/utils/url";

import { CrmIcon } from "../../../components/CrmIcon";
import { ListPerson } from "../../../schema/list";
import { PendingEmailBanner } from "./PendingEmailBanner";
import { CrmLeadTooltip, CrmTooltip } from "./ListPersonHeaderTooltips";
import { ListPersonHeaderFallbackIcons } from "./ListPersonHeaderFallbackIcons";
import { ListPersonHeaderQuickEnrich } from "./listPersonHeader.types";

type Prospect = NonNullable<ListPerson["prospect"]>;
type Offer = ListPerson["offer"];
type Organization = NonNullable<Prospect["organization"]>;

interface ListPersonHeaderProps {
	prospect: Prospect;
	offer?: Offer;
	organization?: Organization | null;
	listPerson?: ListPerson;
	currentCompanyUrl?: string;
	currentEmploymentDateRange: string;
	/** Sorted CV (most recent first) shown behind the "See work history" expander. */
	employments?: WorkHistoryEmployment[];
	/**
	 * Reserve room at the icon strip's right edge for a host-rendered overlay
	 * menu (the Chrome extension's record-options kebab, absolutely positioned
	 * over the card's top-right corner by {@link ContactLeadPanel}).
	 */
	reserveHeaderMenuSpace?: boolean;
	/**
	 * Host-provided control rendered right-aligned in the name row (the feed
	 * run card's "Switch" person switcher). Omitted on the panel surfaces.
	 */
	headerAction?: ReactNode;
	/**
	 * Outer-spacing override of the card container. Defaults to the lead-panel
	 * margins; hosts embedding the card in their own layout (feed run cards)
	 * pass their own spacing instead.
	 */
	className?: string;
	showProspectImage: boolean;
	setShowProspectImage: Dispatch<SetStateAction<boolean>>;
	handleCopyClick: (value: string) => void;
	/**
	 * Opt in to the enrichable phone icon (status badge + click-to-search),
	 * routed through the matching domain's endpoints. When omitted the phone
	 * icon stays a plain `tel:` link (its behaviour on surfaces that don't wire
	 * enrichment, e.g. reused forks).
	 */
	domain?: ContactDomain;
	/**
	 * Click-to-enrich behaviour for the read-only fallback icons (no `domain`).
	 * When set, the email/phone icons run the provided add-then-search chain with
	 * a searching spinner instead of the plain copy/`tel:` fallback. Only consulted
	 * in the no-`domain` branch — surfaces that pass a `domain` use ContactChannels.
	 */
	quickEnrich?: ListPersonHeaderQuickEnrich;
	/**
	 * The name row, handed back to the host so it can tell when the lead's name has scrolled off the
	 * top and the condensed strip has to take over ({@link useCondenseOnScroll}, PREC-4116). Only the
	 * panel surfaces set it.
	 */
	nameRowRef?: Ref<HTMLDivElement>;
}

const getPersonInitials = (prospect: Prospect, offer?: Offer): string => {
	const firstName = prospect.firstName || offer?.firstName || "";
	const lastName = prospect.lastName || offer?.lastName || "";
	const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

	return initials || (prospect.name || offer?.name || "?").charAt(0).toUpperCase();
};

const stopClickThrough = (e: MouseEvent): void => {
	e.stopPropagation();
};

/**
 * Precedence when the prospect and its offer disagree: the first match wins, which is the order the
 * badge has always read. Sendable states are last because `checked` needs a stored address too.
 */
const EMAIL_STATUS_DERIVATION_ORDER = [
	"BOUNCED_VALID",
	"BOUNCED_INVALID",
	"INVALID",
	"RETRY",
	"SEARCHING",
	"VALID",
	"RISKY",
] as const satisfies readonly EmailStatus[];

const deriveEmailIconStatus = (prospect: Prospect, offer?: Offer): EmailIconProps["status"] => {
	const statuses = [toEmailStatus(offer?.emailStatus), toEmailStatus(prospect.emailStatus)];
	const status =
		EMAIL_STATUS_DERIVATION_ORDER.find((candidate) => statuses.includes(candidate)) ?? null;
	const hasSendableEmail =
		(!!offer?.email && isSendableEmailStatus(offer.emailStatus)) ||
		(!!prospect.email && isSendableEmailStatus(prospect.emailStatus));

	// Switch over the shared union so a new status member fails to compile instead of reading as
	// "never searched" (PREC-5035).
	switch (status) {
		case "BOUNCED_VALID":
			return "bounced-valid";
		case "BOUNCED_INVALID":
			return "bounced-invalid";
		case "INVALID":
			return hasSendableEmail ? "checked" : "failed";
		case "RETRY":
			return hasSendableEmail ? "checked" : "retry";
		case "SEARCHING":
			return hasSendableEmail ? "checked" : "searching";
		case "VALID":
		case "RISKY":
		case null:
			return hasSendableEmail ? "checked" : "refresh";
		default:
			return unhandledEmailStatus(status);
	}
};

/**
 * Normalized channel data ({@link ContactChannels}) built from the header's
 * prospect. The enrichment endpoints key by ListPerson id on list/Messages
 * surfaces and by prospect id on Contacts — mirror that split for the `id`.
 */
const buildChannelData = (
	prospect: Prospect,
	offer: Offer | undefined,
	listPerson: ListPerson | undefined,
	domain: ContactDomain
): ContactChannelData => {
	const channelProspect = prospect as Prospect & {
		middleName?: string | null;
		bookingLink?: string | null;
	};
	// The caller's company's email facts (PREC-4262): on the contact itself on Contacts, on the list
	// row on list surfaces; never read off the shared prospect for a list row.
	const facts = (domain === "contact" ? prospect : listPerson) as EmailProvenance | undefined;

	return {
		id: (domain === "contact" ? prospect.id : listPerson?.id) ?? prospect.id,
		name: prospect.name ?? null,
		firstName: prospect.firstName ?? null,
		middleName: channelProspect.middleName ?? null,
		lastName: prospect.lastName ?? null,
		gender: prospect.gender ?? null,
		email: prospect.email ?? offer?.email ?? null,
		emailStatus: prospect.emailStatus ?? offer?.emailStatus ?? null,
		phone: prospect.phone ?? null,
		phoneStatus: prospect.phoneStatus ?? null,
		linkedinUrl: prospect.linkedinUrl ?? null,
		bookingLink: channelProspect.bookingLink ?? null,
		pendingEmails: (prospect.pendingEmails ?? []).map((pe) => ({ id: pe.id, email: pe.email })),
		...emailProvenanceOf(facts, prospect.emailSetAt),
	};
};

/**
 * Compact person identity card (unified-prototype v33 `lp-person`): avatar with
 * the P-score overlaid, name with the channel/CRM status icons inline, role +
 * employment range, location line ending in a "See work history" expander that
 * reveals the About text and full CV. The company itself lives in the
 * {@link CompanyHeader} card below. Plain Tailwind, inline expansion only —
 * safe inside the Chrome extension's shadow root.
 */
export const ListPersonHeader: FunctionComponent<ListPersonHeaderProps> = ({
	prospect,
	offer,
	organization,
	listPerson,
	currentCompanyUrl,
	currentEmploymentDateRange,
	employments = [],
	reserveHeaderMenuSpace = false,
	headerAction,
	className,
	showProspectImage,
	setShowProspectImage,
	handleCopyClick,
	domain,
	quickEnrich,
	nameRowRef,
}) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const initials = useMemo(() => getPersonInitials(prospect, offer), [prospect, offer]);
	const personName = prospect.name || offer?.name || "";
	const personTitle = prospect.title || offer?.title;
	const emailIconStatus = deriveEmailIconStatus(prospect, offer);
	const email = prospect.email || offer?.email;
	const phone = prospect.phone || organization?.phone;
	// Enrichable surfaces (a `domain` is wired) render the unified ContactChannels row — hover
	// tooltips, click-to-search with a spinner, and "Edit contact info". Surfaces without a domain
	// (e.g. a feed person that never resolved to a prospect) fall back to plain read-only icons.
	const channelData = domain ? buildChannelData(prospect, offer, listPerson, domain) : null;
	// The expander's About block shows the LinkedIn headline; the bio (`about`)
	// renders separately as the Overview tab's Summary — different fields.
	const headlineText = prospect.headline?.trim();
	const hasExpandableContent = employments.length > 0 || !!headlineText;

	return (
		<>
			<PendingEmailBanner
				className="mb-4 mx-3"
				listPersonId={listPerson?.id}
				pendingEmails={prospect.pendingEmails}
			/>
			<div
				className={classNames(
					"flex flex-col border border-gray-200 rounded-lg p-3",
					className ?? "w-[100%-8px] mb-2 mx-3"
				)}
			>
				<div className="flex flex-row items-start gap-2.5">
					<div className="relative shrink-0">
						<div className="flex h-[36px] min-w-[36px] max-w-[36px] rounded-full overflow-hidden bg-gray-200">
							{showProspectImage && prospect.image ? (
								<img
									alt={personName}
									className="h-[36px] w-[36px] object-cover"
									src={prospect.image}
									onError={() => setShowProspectImage(false)}
								/>
							) : (
								<div className="h-[36px] w-[36px] bg-gray-200 rounded-full flex items-center justify-center text-base font-semibold text-blue-action">
									{initials}
								</div>
							)}
						</div>
						{/* Positioned wrapper: the badge's Tooltip root is itself `relative`, so
						    absolute classes on the badge would anchor to the tooltip, not the avatar. */}
						<span className="absolute -bottom-1 -right-1.5">
							<ProspectScoreBadge
								className="border-2 border-white"
								overallScore={prospect.scorePersonaCategoryResult?.overallScore}
								reason={prospect.scorePersonaCategoryResult?.overallReasoning}
								scoring={prospect.scorePersonaCategoryResult}
								size="sm"
								variant="solid"
							/>
						</span>
					</div>

					<div className="flex min-w-0 flex-1 flex-col items-start">
						{/* Name + icon strip cluster together (v33 `lp-name-row`), wrapping as needed */}
						<div
							ref={nameRowRef}
							className={`flex w-full flex-row flex-wrap items-center gap-x-1.5 gap-y-1 ${
								reserveHeaderMenuSpace ? "pr-7" : ""
							}`}
						>
							<Tooltip
								className="min-w-0"
								content={prospect.headline || ""}
								disabled={!prospect.headline}
							>
								<button
									className="max-w-full cursor-pointer truncate text-left text-[15px] font-semibold"
									type="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleCopyClick(personName);
									}}
								>
									{personName}
								</button>
							</Tooltip>

							<span className="inline-flex flex-row flex-wrap items-center">
								{channelData ? (
									<ContactChannels data={channelData} domain={domain!} onCopy={handleCopyClick} />
								) : (
									<ListPersonHeaderFallbackIcons
										email={email}
										emailIconStatus={emailIconStatus}
										handleCopyClick={handleCopyClick}
										linkedinUrl={prospect.linkedinUrl}
										phone={phone}
										quickEnrich={quickEnrich}
									/>
								)}
								{currentCompanyUrl && (
									<a
										href={currentCompanyUrl}
										referrerPolicy="no-referrer"
										rel="noreferrer"
										target="_blank"
										onClick={stopClickThrough}
									>
										<SvgIcon className="mr-1 h-[16px] w-auto" svgIcon={ExternalLinkIcon} />
									</a>
								)}
								{prospect.crmProspects?.map((crmProspect) => (
									<Tooltip
										key={crmProspect.id}
										content={<CrmTooltip prospect={prospect} crmProspect={crmProspect} />}
									>
										{crmProspect.url ? (
											<a
												href={ensureHttpProtocol(crmProspect.url)}
												rel="noreferrer"
												target="_blank"
												onClick={stopClickThrough}
											>
												<CrmIcon
													crmType={crmProspect.crmType}
													hasDeal={(prospect.crmDeals?.length ?? 0) > 0}
													syncError={crmProspect.syncError}
													syncStatus={crmProspect.syncStatus}
													url={crmProspect.url ?? ""}
												/>
											</a>
										) : (
											<CrmIcon
												crmType={crmProspect.crmType}
												hasDeal={(prospect.crmDeals?.length ?? 0) > 0}
												syncError={crmProspect.syncError}
												syncStatus={crmProspect.syncStatus}
												url={crmProspect.url ?? ""}
											/>
										)}
									</Tooltip>
								))}
								{prospect.crmLeads?.map((crmLead) => (
									<Tooltip
										key={crmLead.id ?? crmLead.referenceId}
										content={<CrmLeadTooltip crmLead={crmLead} />}
									>
										{crmLead.url ? (
											<a
												href={ensureHttpProtocol(crmLead.url)}
												rel="noreferrer"
												target="_blank"
												onClick={stopClickThrough}
											>
												<CrmIcon
													crmType={crmLead.crmType}
													syncError={crmLead.syncError}
													syncStatus={crmLead.syncStatus}
													url={crmLead.url ?? ""}
												/>
											</a>
										) : (
											<CrmIcon
												crmType={crmLead.crmType}
												syncError={crmLead.syncError}
												syncStatus={crmLead.syncStatus}
												url={crmLead.url ?? ""}
											/>
										)}
									</Tooltip>
								))}
								{listPerson?.connections && listPerson.connections.length > 0 && (
									<Tooltip
										content={listPerson.connections.map((connection) => (
											<div key={connection}>
												{t("contacts.leadPanel.identity.connectedOnLinkedin", {
													name: connection,
												})}
											</div>
										))}
									>
										<div className="flex text-[9px] font-semibold text-white cursor-pointer min-w-[20px] w-[20px] max-w-[20px] h-[20px] items-center justify-center rounded-full bg-brand-light">
											{listPerson.connections.length > 1
												? listPerson.connections.length
												: `${listPerson.connections[0]?.split(" ")[0]?.[0] ?? ""}${listPerson.connections[0]?.split(" ")[1]?.[0] ?? ""}`}
										</div>
									</Tooltip>
								)}
							</span>

							{headerAction && (
								<span className="ml-auto inline-flex shrink-0 self-start">{headerAction}</span>
							)}
						</div>

						{personTitle && (
							<div className="mt-0.5 text-xs font-medium text-text-secondary">
								{personTitle}
								{currentEmploymentDateRange && (
									<>
										<span className="text-text-quaternary"> | </span>
										{currentEmploymentDateRange}
									</>
								)}
							</div>
						)}

						{prospect.scorePersonaCategoryResult?.result && (
							<Tooltip
								content={prospect.scorePersonaCategoryResult.reason || ""}
								disabled={!prospect.scorePersonaCategoryResult.reason}
							>
								<span className="mt-1 text-xs font-medium bg-tag-light rounded-full px-2 py-0.5 text-tag-dark max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">
									{prospect.scorePersonaCategoryResult.result}
								</span>
							</Tooltip>
						)}

						{(prospect.city || prospect.country || hasExpandableContent) && (
							<div className="mt-0.5 flex w-full flex-row flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-text-tertiary">
								<span className="truncate">
									{prospect.city && prospect.country ? `${prospect.city}, ` : prospect.city || ""}
									{prospect.country}
								</span>
								{hasExpandableContent && (
									<ExpandToggle
										expanded={expanded}
										showLabelKey="contacts.leadPanel.identity.seeWorkHistory"
										onToggle={() => setExpanded((value) => !value)}
									/>
								)}
							</div>
						)}
					</div>
				</div>

				{expanded && (
					<div className="mt-2.5 flex flex-col gap-3 rounded-lg bg-brand-white p-3">
						<AboutSection text={headlineText} />
						<WorkHistoryList employments={employments} />
					</div>
				)}
			</div>
		</>
	);
};
